import type { RunDraft } from './cloud-run-draft';
import { validateHomepageTarget } from './cloud-form-validation';

export const maxWalkthroughLength = 2000;
export const maxHandoffFragmentLength = 18000;
export type WalkthroughHandoff = { draft?: RunDraft; error?: string };

export function parseWalkthroughHandoff(hash: string): WalkthroughHandoff {
  if (!hash || hash === '#') return {};
  if (hash.length > maxHandoffFragmentLength) return { error: 'This walkthrough link is too long. Paste the steps below instead.' };
  // URLSearchParams tolerates malformed percent escapes; reject instead of importing damaged steps.
  if (/%(?![a-f\d]{2})/i.test(hash)) return { error: 'This walkthrough link could not be read. Paste the steps below instead.' };
  const values = new URLSearchParams(hash.slice(1));
  const prompt = values.get('task');
  if (!prompt?.trim()) return { error: 'The link has no walkthrough steps. Add the steps you want Rill to follow.' };
  if (prompt.length > maxWalkthroughLength || prompt.includes('\uFFFD') || /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/.test(prompt)) return { error: 'These steps could not be imported. Paste a walkthrough of up to 2,000 characters below.' };
  const suppliedUrl = values.get('url')?.trim() ?? '';
  const validation = suppliedUrl ? validateHomepageTarget(suppliedUrl) : null;
  return { draft: { id: crypto.randomUUID(), url: validation && !validation.message ? validation.target : '', prompt, savedAt: Date.now() }, ...(validation?.message ? { error: 'The steps are loaded. The app URL could not be imported. Add a publicly reachable website URL without credentials or a custom port.' } : {}) };
}

// Run before analytics initializes. The fragment stays in memory only, never in an API request.
export const isWalkthroughDocument = typeof window !== 'undefined' && /^\/freebuff\/?$/.test(window.location.pathname);
export const hasWalkthroughHandoff = isWalkthroughDocument && Boolean(window.location.hash);
const hasTask = hasWalkthroughHandoff && new URLSearchParams(window.location.hash.slice(1)).has('task');
export const initialWalkthrough = hasTask ? parseWalkthroughHandoff(window.location.hash) : {};
if (hasTask) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
